// Shared constants + helpers for the GPU Network (t-hive provider) feature.
// t-hive has 6 decimals — on-chain amounts (amt, totalEarned, pendingBase)
// are stored in BASE units (whole * 10^6). Earning accrues every 30 seconds
// while a node is active: ratePer30s is a whole t-hive/30s UInt.
// ratePerHour = ratePer30s * 120 is kept on the node only for back-compat
// display/aggregation.

export type GpuTier = 'low' | 'mid' | 'high' | 'datacenter';
export type ServerType = 'self' | 'aws';

export const THIVE_DECIMALS = 1_000_000;
export const ACCRUAL_INTERVAL_SECONDS = 30;

export interface TierMeta {
  key: GpuTier;
  label: string;
  ratePer30s: number;
  vramRange: string;
  blurb: string;
}

// Keep these EXACTLY in sync with the backend tier -> rate map
// (partyserver/src/utils/gpuNodes.ts).
export const TIERS: TierMeta[] = [
  { key: 'low', label: 'Low', ratePer30s: 1, vramRange: 'up to 8GB', blurb: 'Laptop GPUs, older consumer cards' },
  { key: 'mid', label: 'Mid', ratePer30s: 2, vramRange: '8–16GB', blurb: 'Mainstream gaming GPUs' },
  { key: 'high', label: 'High', ratePer30s: 4, vramRange: '16–24GB', blurb: 'Prosumer / high-end consumer' },
  {
    key: 'datacenter',
    label: 'Datacenter',
    ratePer30s: 6,
    vramRange: '24GB+',
    blurb: 'A100 / H100-class enterprise silicon',
  },
];

export function getTierMeta(tier: string | undefined | null): TierMeta {
  return TIERS.find((t) => t.key === tier) ?? TIERS[0];
}

/** Suggest a tier from a VRAM size (GB) — the user can always override. */
export function suggestTierFromVram(vram: number): GpuTier {
  if (vram <= 8) return 'low';
  if (vram <= 16) return 'mid';
  if (vram <= 24) return 'high';
  return 'datacenter';
}

export const VRAM_OPTIONS = [8, 12, 16, 24, 48, 80];

export const AWS_REGIONS: { value: string; label: string }[] = [
  { value: 'us-east-1', label: 'US East (N. Virginia)' },
  { value: 'us-west-2', label: 'US West (Oregon)' },
  { value: 'eu-west-1', label: 'EU (Ireland)' },
  { value: 'ap-south-1', label: 'Asia Pacific (Mumbai)' },
  { value: 'ap-southeast-1', label: 'Asia Pacific (Singapore)' },
];

export interface AwsInstanceMeta {
  gpuModel: string;
  vram: number;
  tier: GpuTier;
}

// Starter map — extend as needed. Keep in sync with the backend if this ever
// needs server-side validation of vram/tier values against instance type.
export const AWS_INSTANCE_MAP: Record<string, AwsInstanceMeta> = {
  'g4dn.xlarge': { gpuModel: 'NVIDIA T4', vram: 16, tier: 'mid' },
  'g5.xlarge': { gpuModel: 'NVIDIA A10G', vram: 24, tier: 'high' },
  'g6.xlarge': { gpuModel: 'NVIDIA L4', vram: 24, tier: 'high' },
  'p3.2xlarge': { gpuModel: 'NVIDIA V100', vram: 16, tier: 'high' },
  'g5.12xlarge': { gpuModel: '4x NVIDIA A10G', vram: 96, tier: 'datacenter' },
  'p4d.24xlarge': { gpuModel: '8x NVIDIA A100', vram: 320, tier: 'datacenter' },
  'p5.48xlarge': { gpuModel: '8x NVIDIA H100', vram: 640, tier: 'datacenter' },
};

export const AWS_INSTANCE_TYPES = Object.keys(AWS_INSTANCE_MAP);

/** Convert base units (6 decimals) to a whole t-hive float. */
export function toWholeTHive(baseUnits: number | undefined | null): number {
  return (baseUnits ?? 0) / THIVE_DECIMALS;
}

/** Format base units as a fixed-decimal t-hive string, e.g. 2500000 -> "2.5000" */
export function formatTHive(baseUnits: number | undefined | null, decimals = 4): string {
  return toWholeTHive(baseUnits).toFixed(decimals);
}

/**
 * Live unclaimed t-hive (BASE units) for a node at the given wall-clock
 * time. Mirrors the backend's authoritative accrual formula for display
 * purposes only — the backend always recomputes and is the source of truth
 * on pause/claim.
 */
export function computeDisplayUnclaimedBase(
  node: { pendingBase?: number; status?: string; ratePer30s?: number; activeSince?: number },
  nowSeconds: number
): number {
  const pending = node.pendingBase ?? 0;
  if (node.status !== 'active' || !node.activeSince) return pending;
  const elapsedSeconds = Math.max(0, nowSeconds - node.activeSince);
  const liveWhole = ((node.ratePer30s ?? 0) * elapsedSeconds) / ACCRUAL_INTERVAL_SECONDS;
  return pending + liveWhole * THIVE_DECIMALS;
}

// ── Node reputation (auto-matching engine, display-only) ────────────────
// reputationScore is server-computed basis points (0-10000). NEVER trust the
// client to write this — these helpers only format the already-computed
// value for display.

/** reputationScore (0-10000 basis points) -> 0-100 percentage, rounded. */
export function reputationPercent(reputationScore: number | undefined | null): number {
  return Math.max(0, Math.min(100, Math.round(((reputationScore ?? 0) / 10000) * 100)));
}

/** reputationScore (0-10000 basis points) -> 0-5 star rating, in 0.5 increments. */
export function reputationStars(reputationScore: number | undefined | null): number {
  const pct = reputationPercent(reputationScore);
  return Math.round((pct / 100) * 5 * 2) / 2;
}
